import { InformationCircleIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IsLoading } from "../../../common/components/AppState/IsLoading";
import CardsSelector from "../../../common/components/CardsSelector/CardsSelector";
import { FooterButtons } from "../../../common/components/FooterButtons";
import ManualInterventionIcon from "../../../common/components/Icon/ManualInterventionIcon";
import { Tooltip } from "../../../common/components/Tooltip/Tooltip";
import { useAppDispatch } from "../../../common/state/hooks";
import { getPlatformBuildInfo } from "../../../common/util/getPlatformBuildInfo";
import { IBuild } from "../../../model/model";
import {
  getLatestBuildsOfProjectGroup,
  getNewPublicationSettings,
  setSelectedPlatforms,
} from "../../builds/state/buildsSlice";
import { getSelectedProjectGroup } from "../../projectGroups/state/projectGroupsSlice";
import PlatformCards, { IPurePlatformCard } from "./PlatformCards";

type Props = {
  incrementStep?(): void;
  decrementStep?(): void;
  isTheOnlyPublishingStep?: boolean; // kept for compatibility, but no longer used for submit
};

export const SELECT_PLATFORMS_STEP_COMPONENT_ID = "selectPlatformsStepComponentId";

export default function SelectPlatformsStep({
  incrementStep,
  decrementStep,
  isTheOnlyPublishingStep = false,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const selectedProjectGroup = useSelector(getSelectedProjectGroup);
  const newPublicationSettings = useSelector(getNewPublicationSettings);
  const latestBuildsOfProjectGroup = useSelector(
    getLatestBuildsOfProjectGroup(selectedProjectGroup.id),
  );
  const selectedPlatforms = newPublicationSettings.selectedPlatforms || [];

  const platformCardsWithContext = PlatformCards.map((pc: IPurePlatformCard) => {
    const disabled = isTheOnlyPublishingStep
      ? Boolean(
          latestBuildsOfProjectGroup.find((b: IBuild) => {
            if (b?.scorm_version) {
              return "WebGLApp_scorm" === pc.id;
            }
            return b.target_platform === pc.id;
          }),
        )
      : false;

    return {
      ...pc,
      selected: selectedPlatforms.includes(pc.id),
      disabled,
      component: (
        <div className="relative w-full">
          {pc.component}
          {getPlatformBuildInfo(pc.id)?.isManualBuild && (
            <div className="absolute top-0 right-0 w-6">
              <Tooltip
                message={t("pages.projectGroup.publish.manualInterventionTooltip")}
                classNameChildren="w-64"
                forceSingleLine={false}
              >
                <ManualInterventionIcon />
              </Tooltip>
            </div>
          )}
        </div>
      ),
    };
  });

  const navigateToPublishView = () => navigate(`/courses/${selectedProjectGroup.id}/publish`);

  // Note: previous "please verify" warning effect removed—this step is never the sole step now.

  const timeEstimateForPublication = platformCardsWithContext.reduce((acc, val) => {
    const platformInfo = getPlatformBuildInfo(val.id);
    if (val.selected && platformInfo) {
      return acc + platformInfo.buildDurationInMin;
    }
    return acc;
  }, 0);

  return (
    <div className="flex flex-col grow w-full h-full justify-between">
      <IsLoading
        componentId={SELECT_PLATFORMS_STEP_COMPONENT_ID}
        showSuccess={false}
        spinnerPlaceholder
      >
        <div className="flex flex-col items-center justify-content flex-1 overflow-auto">
          <CardsSelector
            cards={platformCardsWithContext}
            parentClassName="h-full flex flex-row items-center p-2 flex-wrap justify-center gap-4"
            cardClassName="flex flex-col items-center justify-content p-2 w-32 h-20 border-2 select-none rounded-md"
            disabledTooltipText={t("pages.projectGroup.publish.unselectablePlatformTooltip")}
            cardClickCb={(cardId: string) => {
              const clicked = platformCardsWithContext.find((c) => c.id === cardId);
              if (!clicked || clicked.disabled) return;
              const isSelected = selectedPlatforms.includes(cardId);
              const next = isSelected
                ? selectedPlatforms.filter((id) => id !== cardId)
                : [...selectedPlatforms, cardId];
              dispatch(setSelectedPlatforms(next));
            }}
          />

          <div
            className={`text-red-700 m-4 mx-32 flex items-center ${
              platformCardsWithContext.find(
                (e) =>
                  (e.id === "iPhone" ||
                    e.id === "iPad" ||
                    e.id === "MacOS" ||
                    e.id === "WebGLiframe" ||
                    e.id === "WebGLApp" ||
                    e.id === "WebGLApp_scorm") &&
                  e.selected,
              )
                ? ""
                : "invisible"
            }`}
          >
            <div className="mr-2 w-5 h-5">
              <InformationCircleIcon className={"w-full h-full"} />
            </div>
            <h3>{t("pages.projectGroup.publish.contactNeededWarning")}</h3>
          </div>

          <div className="flex justify-end w-full">
            <h6 className="font-bold m-4">
              {timeEstimateForPublication > 0
                ? `${t(
                    "pages.projectGroup.publish.publishDuration",
                  )} ${timeEstimateForPublication} ${t("pages.projectGroup.publish.minutes")}`
                : "\u00A0"}
            </h6>
          </div>
        </div>

        <FooterButtons
          cancelText={t("general.cancel")}
          cancelCb={() => navigateToPublishView()}
          backText={t("general.back")}
          backCb={decrementStep}
          nextText={t("general.next")}
          nextCb={() => incrementStep && incrementStep()}
          nextDisabled={!selectedPlatforms.length}
        />
      </IsLoading>
    </div>
  );
}
